import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Download, 
  Upload, 
  Eye, 
  Trash2,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Clock,
  File,
  Image,
  FileType,
  PenLine,
} from 'lucide-react';
import { DocumentSignModal } from '../components/DocumentSignModal';
import { ModalHeader } from '../components/ModalHeader';
import { PrivacyNotice } from '../components/PrivacyNotice';
import { RootState } from '../store';
import AccountLayout from '../components/AccountLayout';
import { clientDocumentsApi, type ClientDocumentItem } from '../services/clientDocumentsApi';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeRichTextHtml, htmlToText } from '@digitaltattoo/shared-utils';

interface Document {
  id: string;
  source: 'generated' | 'uploaded';
  name: string;
  type: 'identity' | 'medical' | 'other';
  category: string;
  size: number;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  description?: string;
  url: string;
  deleteAllowed: boolean;
  isSigned?: boolean;
  requiredSigners?: string[];
  signedRoles?: string[];
}

interface DocumentFilters {
  source: string;
  type: string;
  status: string;
  category: string;
}

export function DocumentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<DocumentFilters>({
    source: 'all',
    type: 'all',
    status: 'all',
    category: 'all'
  });
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocType, setUploadDocType] = useState<'IDENTITY_CARD' | 'MEDICAL_DOCUMENT' | 'OTHER'>('IDENTITY_CARD');
  const [uploadMeta, setUploadMeta] = useState<Record<string, any>>({});
  const [uploadName, setUploadName] = useState('');
  const [uploadDescriptionHtml, setUploadDescriptionHtml] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [signDoc, setSignDoc] = useState<Document | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryOptions = Array.from(new Set(documents.map(d => d.category))).sort();

  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
  const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    
    loadDocuments();
  }, [isAuthenticated, user, navigate]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const resp = await clientDocumentsApi.list({ page: 1, pageSize: 100 });
      const rows: ClientDocumentItem[] = resp?.data || resp?.data?.data || [];

      const mapped: Document[] = rows.map((d) => {
        const type: Document['type'] =
          d.documentType === 'IDENTITY_CARD'
            ? 'identity'
            : d.documentType === 'MEDICAL_DOCUMENT'
              ? 'medical'
              : 'other';

        return {
          id: d.id,
          source: d.source,
          name: d.name,
          type,
          category: d.category,
          size: typeof d.sizeBytes === 'number' ? d.sizeBytes : 0,
          uploadedAt: d.uploadedAt,
          status: d.status,
          description: d.description,
          url: d.url,
          deleteAllowed: Boolean(d.deleteAllowed),
          isSigned: Boolean(d.isSigned),
          requiredSigners: d.requiredSigners ?? ['CLIENT'],
          signedRoles: d.signedRoles ?? [],
        };
      });

      setDocuments(mapped);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type: string, name: string) => {
    if (name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return Image;
    } else if (name.toLowerCase().match(/\.pdf$/)) {
      return FileType;
    } else {
      return File;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return CheckCircle;
      case 'pending':
        return Clock;
      case 'rejected':
        return AlertCircle;
      default:
        return File;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredDocuments = documents.filter(document => {
    const searchIn = `${document.name} ${document.description ? htmlToText(document.description) : ''}`.toLowerCase();
    const matchesSearch = searchIn.includes(searchTerm.toLowerCase());
    
    const matchesSource = filters.source === 'all' || document.source === filters.source;
    const matchesType = filters.type === 'all' || document.type === filters.type;
    const matchesStatus = filters.status === 'all' || document.status === filters.status;
    const matchesCategory = filters.category === 'all' || document.category === filters.category;
    
    return matchesSearch && matchesSource && matchesType && matchesStatus && matchesCategory;
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    // This flow is metadata-driven; keep it to a single document upload for now.
    const f = files[0];
    if (!f) return;
    setUploadError(null);
    if (f.size > MAX_UPLOAD_BYTES) {
      setSelectedFiles([]);
      setUploadError(t('auth.account.documents.uploadErrorFileTooLarge'));
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(f.type)) {
      setSelectedFiles([]);
      setUploadError(t('auth.account.documents.uploadErrorFileType'));
      return;
    }
    setSelectedFiles([f]);
  };

  const handleDropFile = (file: File) => {
    setUploadError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setSelectedFiles([]);
      setUploadError(t('auth.account.documents.uploadErrorFileTooLarge'));
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setSelectedFiles([]);
      setUploadError(t('auth.account.documents.uploadErrorFileType'));
      return;
    }
    setSelectedFiles([file]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (!uploadName.trim()) {
      setUploadError(t('auth.account.documents.uploadErrorNameRequired'));
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      // Single upload with metadata (first file only)
      const file = selectedFiles[0];
      await clientDocumentsApi.upload({
        file,
        documentType: uploadDocType,
        name: uploadName.trim(),
        descriptionHtml: uploadDescriptionHtml.trim() ? sanitizeRichTextHtml(uploadDescriptionHtml.trim()) : undefined,
        metadata: uploadMeta,
      });
      await loadDocuments();
      setSelectedFiles([]);
      setUploadName('');
      setUploadDescriptionHtml('');
      setShowUploadModal(false);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError((error as any)?.message || t('auth.account.documents.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const fetchWithClientAuth = async (url: string) => {
    const token = localStorage.getItem('client_token');
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : await res.text();
      const message = (data && typeof data === 'object' && 'message' in data) ? (data as any).message : String(data);
      throw new Error(message || 'Failed to fetch document');
    }
    return res;
  };

  const downloadDocument = async (doc: Document) => {
    if (!doc.url) return;
    try {
      const res = await fetchWithClientAuth(doc.url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = (doc.name || 'document').replace(/[^\w\s.-]+/g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const handleDeleteRequest = (doc: Document) => {
    if (doc.source === 'generated' || !doc.deleteAllowed) return;
    setDeleteConfirmDoc(doc);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!deleteConfirmDoc) return;
      setDeleting(true);
      await clientDocumentsApi.deleteUpload(deleteConfirmDoc.id);
      await loadDocuments();
      setDeleteConfirmDoc(null);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleView = (document: Document) => {
    if (!document.url) return;
    const query = new URLSearchParams({
      url: document.url,
      name: document.name || 'document',
    }).toString();
    navigate(`/account/documents/view?${query}`);
  };

  if (loading) {
    return (
      <AccountLayout title={t('auth.account.documents.title')}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-textSecondary">{t('auth.account.documents.loading')}</p>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout 
      title={t('auth.account.documents.title')}
      subtitle={t('auth.account.documents.subtitle')}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.documents.stats.total')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {documents.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.documents.stats.approved')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {documents.filter(doc => doc.status === 'approved').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.documents.stats.pending')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {documents.filter(doc => doc.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.documents.stats.rejected')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {documents.filter(doc => doc.status === 'rejected').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-textSecondary" />
                </div>
                <input
                  type="text"
                  placeholder={t('auth.account.documents.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-textSecondary hover:text-textPrimary hover:bg-gray-50 transition-colors"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('auth.account.documents.filters')}
                {showFilters ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-2" />
                )}
              </button>
            </div>

            {/* Upload Documents */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('auth.account.documents.upload')}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.documents.filterSource')}
                  </label>
                  <select
                    value={filters.source}
                    onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.documents.allSources')}</option>
                    <option value="generated">{t('auth.account.documents.sourceGenerated')}</option>
                    <option value="uploaded">{t('auth.account.documents.sourceUploaded')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.documents.filterType')}
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.documents.allTypes')}</option>
                    <option value="identity">{t('auth.account.documents.uploadTypeIdentity')}</option>
                    <option value="medical">{t('auth.account.documents.uploadTypeMedical')}</option>
                    <option value="other">{t('auth.account.documents.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.documents.filterStatus')}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.documents.allStatuses')}</option>
                    <option value="approved">{t('auth.account.documents.approved')}</option>
                    <option value="pending">{t('auth.account.documents.pending')}</option>
                    <option value="rejected">{t('auth.account.documents.rejected')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.documents.filterCategory')}
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.documents.allCategories')}</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-textPrimary">
              {t('auth.account.documents.documentsList')} ({filteredDocuments.length})
            </h3>
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-textSecondary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-textPrimary mb-2">
                {t('auth.account.documents.noDocuments')}
              </h3>
              <p className="text-textSecondary mb-6">
                {t('auth.account.documents.noDocumentsDesc')}
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <Upload className="h-4 w-4 mr-2" />
                {t('auth.account.documents.uploadFirst')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredDocuments.map((document) => {
                const FileIcon = getFileIcon(document.type, document.name);
                const StatusIcon = getStatusIcon(document.status);
                return (
                  <div key={document.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary-300 to-accent-500 rounded-lg flex items-center justify-center">
                            <FileIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-lg font-medium text-textPrimary truncate">
                              {document.name}
                            </h4>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                            document.source === 'generated'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-purple-50 text-purple-700 border-purple-200'
                          }`}
                          title={document.source === 'generated' ? t('auth.account.documents.sourceGenerated') : t('auth.account.documents.sourceUploaded')}
                        >
                          {document.source === 'generated'
                            ? t('auth.account.documents.sourceGenerated')
                            : t('auth.account.documents.sourceUploaded')}
                        </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {t(`auth.account.documents.${document.status}`)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-textSecondary">
                            <span>{document.category}</span>
                            {document.size > 0 ? (
                              <>
                                <span>•</span>
                                <span>{formatFileSize(document.size)}</span>
                              </>
                            ) : null}
                            <span>•</span>
                            <span>{new Date(document.uploadedAt).toLocaleDateString()}</span>
                          </div>
                          {document.description && (
                            <div
                              className="text-sm text-textSecondary mt-1 max-w-none max-h-16 overflow-hidden
                                [&_ul]:list-disc [&_ul]:pl-5
                                [&_ol]:list-decimal [&_ol]:pl-5
                                [&_li]:my-0.5
                                [&_p]:my-0.5
                              "
                              dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(document.description) }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {document.source === 'generated' && document.isSigned && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3" />
                            Signé
                          </span>
                        )}
                        {document.source === 'generated' &&
                          (document.requiredSigners ?? ['CLIENT']).includes('CLIENT') &&
                          !(document.signedRoles ?? []).includes('CLIENT') && (
                            <button
                              onClick={() => setSignDoc(document)}
                              className="p-2 text-textSecondary hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Signer ce document"
                            >
                              <PenLine className="h-4 w-4" />
                            </button>
                          )}
                        <button
                          onClick={() => handleView(document)}
                          className="p-2 text-textSecondary hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title={t('auth.account.documents.view')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => void downloadDocument(document)}
                          className="p-2 text-textSecondary hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t('auth.account.documents.download')}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {document.deleteAllowed && (
                          <button
                            onClick={() => handleDeleteRequest(document)}
                            className="p-2 text-textSecondary hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('auth.account.documents.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowUploadModal(false)}></div>
            
            <div className="modal-panel inline-block align-bottom rounded-lg text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <ModalHeader
                  title={t('auth.account.documents.uploadDocuments')}
                  onClose={() => setShowUploadModal(false)}
                  className="mb-4"
                />
                
                <div className="space-y-4">
                  <PrivacyNotice
                    variant={
                      uploadDocType === 'IDENTITY_CARD'
                        ? 'uploadIdentity'
                        : uploadDocType === 'MEDICAL_DOCUMENT'
                          ? 'uploadMedical'
                          : 'uploadOther'
                    }
                  />
                  {uploadError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {uploadError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('auth.account.documents.selectFiles')}
                    </label>
                    <div className="grid grid-cols-1 gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          {t('auth.account.documents.uploadName')}
                        </label>
                        <input
                          type="text"
                          value={uploadName}
                          onChange={(e) => setUploadName(e.target.value)}
                          className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          placeholder={t('auth.account.documents.uploadNamePlaceholder')}
                        />
                        <p className="mt-1 text-xs text-textSecondary">
                          {t('auth.account.documents.uploadNameHint')}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          {t('auth.account.documents.uploadType')}
                        </label>
                        <select
                          value={uploadDocType}
                          onChange={(e) => {
                            const next = e.target.value as any;
                            setUploadDocType(next);
                            setUploadMeta({});
                          }}
                          className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="IDENTITY_CARD">{t('auth.account.documents.uploadTypeIdentity')}</option>
                          <option value="MEDICAL_DOCUMENT">{t('auth.account.documents.uploadTypeMedical')}</option>
                          <option value="OTHER">{t('auth.account.documents.uploadTypeOther')}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          {t('auth.account.documents.uploadDescription')}
                        </label>
                        <RichTextEditor
                          value={uploadDescriptionHtml}
                          onChange={setUploadDescriptionHtml}
                          disabled={uploading}
                          placeholder={t('auth.account.documents.uploadDescriptionPlaceholder')}
                        />
                      </div>

                      {uploadDocType === 'IDENTITY_CARD' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-textPrimary mb-1">
                              {t('auth.account.documents.identitySide')}
                            </label>
                            <select
                              value={uploadMeta.documentSide || ''}
                              onChange={(e) => setUploadMeta((prev) => ({ ...prev, documentSide: e.target.value }))}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            >
                              <option value="">{t('auth.account.documents.identitySideSelect')}</option>
                              <option value="front">{t('auth.account.documents.identitySideFront')}</option>
                              <option value="back">{t('auth.account.documents.identitySideBack')}</option>
                              <option value="both">{t('auth.account.documents.identitySideBoth')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-textPrimary mb-1">
                              {t('auth.account.documents.identityExpiryDate')}
                            </label>
                            <input
                              type="date"
                              value={uploadMeta.expiryDate || ''}
                              onChange={(e) => setUploadMeta((prev) => ({ ...prev, expiryDate: e.target.value }))}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {uploadDocType === 'MEDICAL_DOCUMENT' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-textPrimary mb-1">
                              {t('auth.account.documents.medicalIssuedAt')}
                            </label>
                            <input
                              type="date"
                              value={uploadMeta.issuedAt || ''}
                              onChange={(e) => setUploadMeta((prev) => ({ ...prev, issuedAt: e.target.value }))}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-textPrimary mb-1">
                              {t('auth.account.documents.medicalDoctorName')}
                            </label>
                            <input
                              type="text"
                              value={uploadMeta.doctorName || ''}
                              onChange={(e) => setUploadMeta((prev) => ({ ...prev, doctorName: e.target.value }))}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                        isDraggingFile ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDraggingFile(true);
                      }}
                      onDragLeave={() => setIsDraggingFile(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDraggingFile(false);
                        const f = e.dataTransfer.files?.[0];
                        if (f) handleDropFile(f);
                      }}
                    >
                      <p className="text-sm font-medium text-textPrimary">
                        {t('auth.account.documents.dropzoneTitle')}
                      </p>
                      <p className="text-xs text-textSecondary mt-1">
                        {t('auth.account.documents.dropzoneHint')}
                      </p>
                      <p className="text-xs text-textSecondary mt-1">
                        {t('auth.account.documents.dropzoneLimits')}
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  
                  {selectedFiles.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-textPrimary mb-2">
                        {t('auth.account.documents.selectedFiles')} ({selectedFiles.length})
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm text-textPrimary truncate">{file.name}</span>
                            <span className="text-xs text-textSecondary">{formatFileSize(file.size)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:items-center gap-2">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('auth.account.documents.uploading')}
                    </>
                  ) : (
                    t('auth.account.documents.upload')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="btn-secondary w-full sm:w-auto"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature modal */}
      {signDoc && (
        <DocumentSignModal
          documentId={signDoc.id}
          documentName={signDoc.name}
          pdfUrl={signDoc.url}
          defaultSignerName={user ? `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() || (user as any).name || '' : ''}
          availableRoles={['CLIENT']}
          authTokenKey="client_token"
          onSuccess={() => {
            setSignDoc(null);
            void loadDocuments();
          }}
          onClose={() => setSignDoc(null)}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirmDoc && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => (deleting ? null : setDeleteConfirmDoc(null))}
            ></div>

            <div className="modal-panel inline-block align-bottom rounded-lg text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <ModalHeader
                  title={t('auth.account.documents.deleteConfirmTitle')}
                  onClose={() => {
                    if (!deleting) setDeleteConfirmDoc(null);
                  }}
                  disabled={deleting}
                  className="mb-4"
                />

                <p className="text-sm text-textSecondary">
                  {t('auth.account.documents.deleteConfirmMessage', { name: deleteConfirmDoc.name })}
                </p>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleConfirmDelete()}
                  disabled={deleting}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm"
                >
                  {deleting ? t('auth.account.documents.deleting') : t('auth.account.documents.deleteConfirmAction')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmDoc(null)}
                  disabled={deleting}
                  className="btn-secondary w-full sm:w-auto"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AccountLayout>
  );
}

export default DocumentsPage;
